import type { TranslationKey } from 'intl/types/translationKey';
import type { IntlShape } from 'react-intl';
import type {
  ChartFiltersUnion,
  ChartType,
  InspectionChart,
  IssueChart,
} from 'shared/types/analytics';
import type { UserInDto } from 'shared/domain/user/types/inDto';
import type { ChartColorPalette } from '../colors';
import type { ChartTokens } from '../tokens';

export enum ChartResultStatus {
  idle = 'idle',
  loading = 'loading',
  success = 'success',
  error = 'error',
}

export type ChartSize = 'small' | 'large';
export type ChartBuilderKey = string;
export type ChartRendererKey = ChartType;

/** Role check in catalog; hook enforces process/field rules via ChartResult.status. */
export type ChartAccessMode = 'role' | 'hook';

export type ChartDefinition = Readonly<{
  id: string;
  section: string;
  order: number;
  type: ChartType;
  size: ChartSize;
  titleId: TranslationKey;
  subtitleId?: TranslationKey;
  builderKey: ChartBuilderKey;
  rendererKey: ChartRendererKey;
  enabled: boolean;
  accessMode: ChartAccessMode;
  canAccess?: (user: UserInDto) => boolean;
  issueChartKey?: IssueChart;
  inspectionChartKey?: InspectionChart;
}>;

export type ChartSliceLabel = string | Readonly<{ labelId: string }>;

export type ChartLineSeries = Readonly<{
  label: string;
  values: readonly number[];
}>;

export type ChartRawData = Readonly<{
  values: readonly number[];
  labels: readonly ChartSliceLabel[];
  lineSeries?: readonly ChartLineSeries[];
  categories?: readonly string[];
  tripletValues?: readonly (number | undefined)[];
  tripletLabels?: readonly ChartSliceLabel[];
}>;

export type ChartResult<F extends ChartFiltersUnion = ChartFiltersUnion> =
  Readonly<{
    status: ChartResultStatus;
    rawData: ChartRawData | null;
    filters: F | undefined;
    dataGeneratedAt: number | null;
    error: string | null;
    reload: () => void;
  }>;

export type ChartData = Readonly<{
  values: readonly number[];
  labels: readonly string[];
  lineSeries?: readonly ChartLineSeries[];
  categories?: readonly string[];
  tripletValues?: readonly (number | undefined)[];
  tripletLabels?: readonly string[];
}>;

export type ChartViewModel = Readonly<{
  type: ChartType;
  isEmpty: boolean;
}>;

export type AnalyticsChart = Readonly<{
  id: string;
  section: string;
  status: ChartResultStatus;
  viewModel: ChartViewModel;
  error: string | null;
  filters?: ChartFiltersUnion;
  dataGeneratedAt: number | null;
}>;

export type ChartHook = () => ChartResult;

export type ChartFetchResult = ChartResult &
  Readonly<{
    toChartResult?: () => ChartResult;
  }>;

export type ChartDataBuilder = (
  definition: ChartDefinition,
  result: ChartResult,
  ctx: BuildChartContext
) => ChartData;

export type BuildChartContext = Readonly<{
  intl: IntlShape;
  variant: 'dashboard' | 'details';
  locale: string;
  timezone: string;
  tokens: Readonly<ChartTokens>;
  colors: Readonly<ChartColorPalette>;
}>;
